// frontend/packages/business-console/src/pages/InvoiceDetail.jsx
//
// Work item 2.17a — Invoice detail page
// (`/business/:slug/invoices/:id`). Spec source:
// `docs/design/quotes-invoices-console.md` §3.4 + §3.6.
//
// Mirror of QuoteDetail with invoice-specific additions:
//   - Invoice-state action bar (Send / Resend / Void / Copy link)
//   - Payment-status panel in the right rail (lists CrmPayment rows;
//     refunds nested, disputes surfaced with a banner)
//   - Activity timeline weaves payment events alongside state
//     transitions
//
// Mark-Paid is intentionally NOT wired in v1 — the backend endpoint
// for manual mark-paid lives in a follow-up (deferred from 2.12 per
// design doc Out-of-v1). When that lands, add a "Mark paid manually"
// action here.

import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { getApiBase } from '@upryzze/services';
import { BusinessContext } from '../contexts/BusinessContext';
import { useInvoiceDetail } from './Invoices/useInvoiceDetail';
import InvoiceDocumentView from './Invoices/InvoiceDocumentView';
import InvoicePaymentPanel from './Invoices/InvoicePaymentPanel';
import InvoiceActivityTimeline from './Invoices/InvoiceActivityTimeline';
import {
  deleteInvoiceDraft,
  resendInvoiceReceipt,
  sendInvoice,
  voidInvoice,
} from '../services/invoiceService';

const STATE_COLOR = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  partial: 'warning',
  paid: 'success',
  overdue: 'error',
  void: 'default',
};

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function buildPublicLink(viewToken) {
  if (!viewToken) return null;
  if (typeof window === 'undefined') return `/inv/${viewToken}`;
  return `${window.location.origin}/inv/${viewToken}`;
}

// PDF lives on the BACKEND host at /inv/:token/pdf (302 -> SAS-signed
// blob). Linking to invoice.pdf.blobUrl directly hits the private blob
// with no token -> 404. Build from the API base origin.
function buildPdfDownloadUrl(viewToken) {
  if (!viewToken) return null;
  try {
    const origin = new URL(getApiBase(), window.location.origin).origin;
    return `${origin}/inv/${viewToken}/pdf`;
  } catch {
    return `/inv/${viewToken}/pdf`;
  }
}

export default function InvoiceDetail() {
  const { business } = useContext(BusinessContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const {
    invoice,
    customer,
    payments,
    availableActions,
    business: demoBusiness,
    loading,
    error,
    refresh,
    isDemo,
  } = useInvoiceDetail(id);

  const [pendingAction, setPendingAction] = useState(null); // 'send' | 'void' | 'delete' | 'resendReceipt'
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [toast, setToast] = useState(null);

  const closeDialog = () => setPendingAction(null);

  const handleConfirm = async () => {
    if (!pendingAction || !invoice) return;
    setActing(true);
    setActionError(null);
    try {
      if (pendingAction === 'send') {
        await sendInvoice(invoice._id);
        setToast({
          severity: 'success',
          text: `${invoice.number} sent.`,
        });
      } else if (pendingAction === 'void') {
        await voidInvoice(invoice._id);
        setToast({ severity: 'info', text: 'Invoice voided.' });
      } else if (pendingAction === 'delete') {
        await deleteInvoiceDraft(invoice._id);
        setToast({ severity: 'info', text: 'Draft deleted.' });
        navigate(`${basePath}/invoices`);
        return;
      } else if (pendingAction === 'resendReceipt') {
        await resendInvoiceReceipt(invoice._id);
        setToast({
          severity: 'success',
          text: 'Receipt re-sent to customer.',
        });
      }
      closeDialog();
      refresh();
    } catch (err) {
      setActionError(
        err?.response?.data?.error || err?.message || 'Action failed.'
      );
    } finally {
      setActing(false);
    }
  };

  const handleCopyLink = async () => {
    const link = buildPublicLink(invoice?.viewToken);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setToast({ severity: 'success', text: 'Public link copied.' });
    } catch {
      setToast({
        severity: 'error',
        text: "Couldn't copy — try selecting the URL manually.",
      });
    }
  };

  // ── Loading / error / not-found shells ─────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/invoices`} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.response?.data?.error ||
            error?.message ||
            "Couldn't load this invoice."}
        </Alert>
      </Box>
    );
  }
  if (!invoice) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/invoices`} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          Invoice not found.
        </Alert>
      </Box>
    );
  }

  const a = availableActions || {};
  const publicLink = buildPublicLink(invoice.viewToken);
  const isOverdue = invoice.state === 'overdue';
  const hasDispute = (payments || []).some((p) => p.state === 'disputed');

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Action bar / breadcrumb ──────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BackLink to={`${basePath}/invoices`} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            /
          </Typography>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {invoice.number}
          </Typography>
          <Chip
            size="small"
            label={invoice.state}
            color={STATE_COLOR[invoice.state] || 'default'}
            variant="outlined"
            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
          />
          {isDemo ? (
            <Chip
              size="small"
              label="Demo"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', gap: 1, rowGap: 1 }}
        >
          {a.canEdit ? (
            <Button
              size="small"
              startIcon={<EditOutlinedIcon />}
              component={RouterLink}
              to={`${basePath}/invoices/${invoice._id}/edit`}
            >
              Edit
            </Button>
          ) : null}
          {a.canSend ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              onClick={() => setPendingAction('send')}
            >
              Send
            </Button>
          ) : null}
          {a.canResend ? (
            <Button
              size="small"
              startIcon={<SendOutlinedIcon />}
              onClick={() => setPendingAction('send')}
            >
              {isOverdue ? 'Send reminder' : 'Resend'}
            </Button>
          ) : null}
          {publicLink ? (
            <Tooltip title={publicLink}>
              <Button
                size="small"
                startIcon={<LinkRoundedIcon />}
                onClick={handleCopyLink}
              >
                Copy link
              </Button>
            </Tooltip>
          ) : null}
          {invoice.state === 'paid' ? (
            <Button
              size="small"
              startIcon={<MarkEmailReadOutlinedIcon />}
              onClick={() => setPendingAction('resendReceipt')}
            >
              Resend receipt
            </Button>
          ) : null}
          {a.canVoid ? (
            <Button
              size="small"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => setPendingAction('void')}
            >
              Void
            </Button>
          ) : null}
          {a.canDelete ? (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => setPendingAction('delete')}
            >
              Delete
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* ── Overdue banner ───────────────────────────────────────── */}
      {isOverdue ? (
        <Alert severity="error">
          This invoice is overdue. Outstanding:{' '}
          <strong>{fmtUsd(invoice.amountDue)}</strong>
          {invoice.dueAt
            ? ` (due ${new Date(invoice.dueAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })})`
            : ''}
          . Send a reminder or follow up directly.
        </Alert>
      ) : null}

      {/* ── Dispute banner (also surfaced inside payment panel; */}
      {/*    top-of-page version makes it impossible to miss) ───── */}
      {hasDispute ? (
        <Alert severity="error">
          A payment on this invoice is being disputed. Respond in the Stripe
          Dashboard — the response deadline matters.
        </Alert>
      ) : null}

      {/* ── Body: document + right rail (timeline + payments) ──── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 300px' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <InvoiceDocumentView
            invoice={invoice}
            customer={customer}
            business={demoBusiness || { name: business?.name || '' }}
          />
        </Box>
        <Stack
          spacing={1.5}
          sx={{ position: { md: 'sticky' }, top: { md: 16 } }}
        >
          <InvoicePaymentPanel
            payments={payments}
            invoiceId={invoice._id}
            onRefundIssued={refresh}
          />
          <InvoiceActivityTimeline invoice={invoice} payments={payments} />
          {invoice.pdf?.blobUrl && invoice.viewToken ? (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              component="a"
              href={buildPdfDownloadUrl(invoice.viewToken)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download sent PDF
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* ── Confirmation dialogs ─────────────────────────────────── */}
      <Dialog open={pendingAction === 'send'} onClose={closeDialog}>
        <DialogTitle>Send {invoice.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends the <strong>PDF and a payment link</strong> to{' '}
            <strong>{customerName(customer)}</strong>
            {customer?.email ? (
              <>
                {' '}
                (<code>{customer.email}</code>)
              </>
            ) : null}
            . Once sent, the invoice is locked — you can void it later but you
            can&apos;t unsend.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
          >
            {acting ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'resendReceipt'} onClose={closeDialog}>
        <DialogTitle>Resend receipt for {invoice.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This fires the payment-receipt email to{' '}
            <strong>{customer?.email || 'the customer’s billing email'}</strong>
            . Useful if they didn&apos;t receive the original, or if you just
            updated their address.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? (
                <CircularProgress size={14} />
              ) : (
                <MarkEmailReadOutlinedIcon />
              )
            }
          >
            {acting ? 'Sending…' : 'Resend receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'void'} onClose={closeDialog}>
        <DialogTitle>Void {invoice.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voiding marks the invoice as cancelled and removes it from
            outstanding totals. Existing payments on it are not refunded
            automatically — handle refunds in the Stripe Dashboard if needed.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? <CircularProgress size={14} /> : <CancelOutlinedIcon />
            }
          >
            {acting ? 'Working…' : 'Void invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'delete'} onClose={closeDialog}>
        <DialogTitle>Delete draft {invoice.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the draft. You can restore it from your audit log
            if needed.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? (
                <CircularProgress size={14} />
              ) : (
                <DeleteOutlineRoundedIcon />
              )
            }
          >
            {acting ? 'Deleting…' : 'Delete draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || 'info'}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}

function BackLink({ to }) {
  return (
    <Tooltip title="Back to invoices">
      <IconButton
        size="small"
        component={RouterLink}
        to={to}
        sx={{ color: 'text.secondary' }}
        aria-label="Back to invoices"
      >
        <ArrowBackRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function customerName(customer) {
  if (!customer) return 'the customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  const joined = `${fn} ${ln}`.trim();
  return joined || 'the customer';
}
